import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import MenuDrawer from './MenuDrawer.jsx';
import { PROPERTY_PATH } from './paths.js';

// Whether the burger menu's drawer is open, held ABOVE the screens rather than in
// the button that opens it. The Property screen is preceded by a skeleton with
// the same header while the account and its homes load, and the two are different
// component trees: a drawer opened from the skeleton's button used to unmount —
// vanish, mid-use — the moment the real screen replaced it. Held here, at the
// gated layout, the drawer outlives that swap; the button anywhere below only
// asks for it to open.
//
// The drawer is the Property screen's, so it closes when the route LEAVES the
// Property screen (choosing Help, the browser's Back) — and not before. The
// Property screen has two addresses: the landing `/`, which the post-sign-in
// screen sits on until the homes arrive, and `/property/:homeId`, which the
// landing then redirects to. A drawer opened during that wait must survive the
// redirect (seen live: it did not, when any route change closed it). Leaving is
// also the ONLY way choosing Help closes it: React Router navigates in a
// transition, so a close on the click itself would commit before the route
// change and hand focus back to a button about to unmount. Closing on the new
// route instead, in a layout effect so the drawer never paints over the new
// page, lets the dismiss hook's focus restore fall through to the new screen's
// heading.
//
// The default value is inert on purpose: a screen rendered without the provider
// (a unit test of the screen alone) still shows its menu button, which just does
// nothing there. The provider is mounted once, in GatedLayout, so every signed-in
// screen has a working one.
const noop = () => {};
export const MenuContext = createContext({
  open: false,
  openMenu: noop,
  closeMenu: noop,
});

export function useMenu() {
  return useContext(MenuContext);
}

export default function MenuProvider({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onProperty =
    pathname === PROPERTY_PATH ||
    matchPath({ path: '/property/:homeId', end: true }, pathname) !== null;

  useLayoutEffect(() => {
    if (!onProperty) {
      setOpen(false);
    }
  }, [onProperty]);

  const openMenu = useCallback(() => setOpen(true), []);
  const closeMenu = useCallback(() => setOpen(false), []);
  const value = useMemo(
    () => ({ open, openMenu, closeMenu }),
    [open, openMenu, closeMenu],
  );

  return (
    <MenuContext.Provider value={value}>
      {children}
      {open ? <MenuDrawer onClose={closeMenu} /> : null}
    </MenuContext.Provider>
  );
}
